import { mergeCampaignConfig } from './defaults';
import type {
  AssignCouponRequest,
  AssignCouponResponse,
  CampaignConfig,
  CreateLeadRequest,
  CreateLeadResponse,
  SpinWheelRequest,
  SpinWheelResponse,
  TrackAnnouncementEventRequest,
  TrackEventRequest,
  WidgetRuntimeOptions,
} from './types';

const requestTimeoutMs = 10000;

export class SpinWheelApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'SpinWheelApiError';
    this.status = status;
  }
}

export class SpinWheelApiClient {
  private readonly baseUrl: string;

  constructor(options: WidgetRuntimeOptions) {
    this.baseUrl = options.apiBaseUrl.replace(/\/+$/, '');
  }

  async getCampaignConfig(options: WidgetRuntimeOptions): Promise<CampaignConfig> {
    const params = new URLSearchParams();
    if (options.shopDomain) params.set('shopDomain', options.shopDomain);
    if (options.locale) params.set('locale', options.locale);
    if (options.fingerprint) params.set('fingerprint', options.fingerprint);
    if (options.preview) params.set('preview', 'true');

    const config = await this.request<Partial<CampaignConfig>>(
      `/campaign-config?${params.toString()}`,
      {
        method: 'GET',
      },
    );

    return mergeCampaignConfig(config);
  }

  createLead(payload: CreateLeadRequest): Promise<CreateLeadResponse> {
    return this.request<CreateLeadResponse>('/create-lead', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  spinWheel(payload: SpinWheelRequest): Promise<SpinWheelResponse> {
    return this.request<SpinWheelResponse>('/spin-wheel', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  assignCoupon(payload: AssignCouponRequest): Promise<AssignCouponResponse> {
    return this.request<AssignCouponResponse>('/assign-coupon', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  trackEvent(payload: TrackEventRequest): Promise<void> {
    return this.request<void>('/analytics', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  trackAnnouncementEvent(payload: TrackAnnouncementEventRequest): Promise<void> {
    return this.request<void>('/announcement-analytics', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  private async request<T>(path: string, init: RequestInit): Promise<T> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), requestTimeoutMs);

    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        ...init,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...init.headers,
        },
        credentials: 'omit',
        mode: 'cors',
        signal: controller.signal,
      });

      const text = await response.text();
      const body = text ? JSON.parse(text) : {};

      if (!response.ok) {
        throw new SpinWheelApiError(body.message || 'Spin wheel request failed.', response.status);
      }

      return body as T;
    } catch (error) {
      if (error instanceof SpinWheelApiError) throw error;
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new SpinWheelApiError('The promotion service timed out.', 408);
      }
      throw new SpinWheelApiError('Unable to reach the promotion service.', 0);
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
